import { useCallback, useEffect, useState } from "react";
import { useDragStore } from "@/stores/useDragStore";
import { isFileDrag } from "./file-drag";
import type { DragPayload } from "./payload";

interface DropTargetOptions<K extends DragPayload["kind"]> {
	/** 受け入れるペイロード種別 (複数なら配列)。一致しないドラッグは無視する。 */
	accept: K | readonly K[];
	/** この payload をここにドロップできるか。false の間はホバー表示も出さない。 */
	canDrop: (payload: Extract<DragPayload, { kind: K }>) => boolean;
	/** ドロップ確定時の処理。await されないため、内部で例外を握りつぶさずトースト等で通知する。 */
	onDrop: (payload: Extract<DragPayload, { kind: K }>, e: React.DragEvent) => void;
	/** アプリ内のドラッグを受けるときの dropEffect。既定は "move"。 */
	payloadDropEffect?:
		| "move"
		| "copy"
		| ((payload: Extract<DragPayload, { kind: K }>) => "move" | "copy");
	/**
	 * 受け入れ可能なドラッグが dragover 中のときに呼ばれる (accepted 成立後のみ)。
	 * dragover 中はポインタ座標が現行 API で取れないため、ドロップ位置に応じたインジケータ
	 * 表示や before/after 判定など座標が必要な処理に使う。
	 */
	onDragOverAccepted?: (payload: Extract<DragPayload, { kind: K }>, e: React.DragEvent) => void;
	/**
	 * OS からのファイルドラッグの受け入れ。省略時・canDrop=false 時も preventDefault +
	 * dropEffect "none" とし、ブラウザがファイルを開いて画面遷移する事故を防ぐ。
	 */
	acceptFiles?: {
		/** dragover 中は dataTransfer の中身を読めないため、ドラッグ内容によらない可否だけを判定する。 */
		canDrop: () => boolean;
		onDrop: (e: React.DragEvent) => void;
	};
}

/** dragover 中のドラッグ種別。files はホバー中の OS ファイルドラッグ。 */
type DragOverKind = "none" | "payload" | "files";

export interface DragOverAction {
	preventDefault: boolean;
	/** null はブラウザ既定のまま触らないことを表す。 */
	dropEffect: "move" | "copy" | "none" | null;
	over: DragOverKind;
}

/**
 * dragover 中のドラッグ種別と受け入れ可否から、イベントへ適用する操作を決める純粋関数。
 * OS ファイルドラッグは拒否時も preventDefault + dropEffect "none" にして、ブラウザが
 * ドロップされたファイルを開いてしまう遷移事故を防ぐ。
 */
export function resolveDragOverAction(input: {
	isFileDrag: boolean;
	filesAccepted: boolean;
	payloadAccepted: boolean;
	payloadDropEffect?: "move" | "copy";
}): DragOverAction {
	if (input.isFileDrag) {
		return input.filesAccepted
			? { preventDefault: true, dropEffect: "copy", over: "files" }
			: { preventDefault: true, dropEffect: "none", over: "none" };
	}
	return input.payloadAccepted
		? { preventDefault: true, dropEffect: input.payloadDropEffect ?? "move", over: "payload" }
		: { preventDefault: false, dropEffect: null, over: "none" };
}

/**
 * 任意の要素をドロップ先にする DOM props (dropProps) と、受け入れ可能なドラッグが
 * 重なっているかを示す isOver を返す。dragover 中は dataTransfer の値を読めないため、
 * 判定はドラッグストアの現在ペイロード (OS ファイルドラッグでは acceptFiles.canDrop) で行う。
 */
export function useDropTarget<K extends DragPayload["kind"]>(opts: DropTargetOptions<K>) {
	const [over, setOver] = useState<DragOverKind>("none");

	const matched = useDragStore((s) => {
		const kind = s.payload?.kind;
		const accepted =
			typeof opts.accept === "string"
				? kind === opts.accept
				: (opts.accept as readonly string[]).includes(kind ?? "");
		return accepted ? (s.payload as Extract<DragPayload, { kind: K }>) : null;
	});
	const accepted = matched !== null && opts.canDrop(matched);
	const filesAccepted = opts.acceptFiles?.canDrop() ?? false;

	const onDragOver = useCallback(
		(e: React.DragEvent) => {
			/** 内側のドロップ先で処理し、祖先のドロップ先との二重反応を防ぐ。 */
			e.stopPropagation();
			/** ドラッグ中の状態変更を反映するため、受け入れ可否をイベントごとに評価する。 */
			const action = resolveDragOverAction({
				isFileDrag: isFileDrag(e),
				filesAccepted: opts.acceptFiles?.canDrop() ?? false,
				payloadAccepted: accepted && matched !== null,
				payloadDropEffect:
					typeof opts.payloadDropEffect !== "function"
						? opts.payloadDropEffect
						: matched === null
							? undefined
							: opts.payloadDropEffect(matched),
			});
			setOver(action.over);
			/** drop を発火させる対象だけブラウザの既定動作を止める。 */
			if (!action.preventDefault) return;
			e.preventDefault();
			if (action.dropEffect !== null) e.dataTransfer.dropEffect = action.dropEffect;
			if (action.over === "payload" && matched !== null) opts.onDragOverAccepted?.(matched, e);
		},
		[accepted, matched, opts],
	);

	const onDragLeave = useCallback(() => setOver("none"), []);

	const onDrop = useCallback(
		(e: React.DragEvent) => {
			e.stopPropagation();
			setOver("none");
			if (isFileDrag(e)) {
				/** 拒否時にもブラウザがファイルを開かないよう既定動作を止める。 */
				e.preventDefault();
				if (opts.acceptFiles?.canDrop()) opts.acceptFiles.onDrop(e);
				return;
			}
			if (!accepted || matched === null) return;
			e.preventDefault();
			try {
				opts.onDrop(matched, e);
			} finally {
				/** ドロップで元の要素がアンマウントされると dragend が届かないため、ここで終える。 */
				useDragStore.getState().endDrag();
			}
		},
		[accepted, matched, opts],
	);

	/** ウィンドウ外へ抜けた OS ファイルドラッグのハイライトも確実に解除する。 */
	useEffect(() => {
		if (over !== "files") return;
		const reset = () => setOver("none");
		const handleWindowDragLeave = (e: DragEvent) => {
			/** relatedTarget がない場合だけドキュメント外への離脱として扱う。 */
			if (e.relatedTarget === null) reset();
		};
		window.addEventListener("dragend", reset);
		window.addEventListener("drop", reset);
		window.addEventListener("blur", reset);
		window.addEventListener("dragleave", handleWindowDragLeave);
		return () => {
			window.removeEventListener("dragend", reset);
			window.removeEventListener("drop", reset);
			window.removeEventListener("blur", reset);
			window.removeEventListener("dragleave", handleWindowDragLeave);
		};
	}, [over]);

	return {
		isOver: over === "payload" ? accepted : over === "files" && filesAccepted,
		dropProps: { onDragOver, onDragLeave, onDrop },
	};
}
